import { Component, OnInit, Input, Output, EventEmitter, inject, OnDestroy } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormGroupDirective,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatDividerModule } from "@angular/material/divider";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSelectModule } from "@angular/material/select";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import { Subject, takeUntil, debounceTime, filter, take } from "rxjs";
import { selectActivePacket, selectEffectiveQuestionnaire } from "@store/selectors/mips-packet/mips-packet.selector";
import { MipsQuestionnaireActions } from "@store/actions/mips-questionnaire/mips-questionnaire.actions";
import { MipsEligibilityActions } from "@store/actions/mips-eligibility/mips-eligibility.actions";
import { atLeastOneCheckboxValidator } from "@directives/at-least-one-checkbox.validator";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import { toSignal } from "@angular/core/rxjs-interop";
import { MipsQuestionnaire } from "@models/mips-stepper.model";
import { ROLE_GROUPS } from "../../constants/roles";
import { getCurrentPerformanceYear, getAvailablePerformanceYears, isSubmissionsClosed, REPORTING_TYPES } from "../../constants/mips-constants";
import { UserService } from "@services/user.service";

@Component({
  selector: "app-mips-questionnaire",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: "./mips-questionnaire.component.html",
  styleUrls: ["./mips-questionnaire.component.css"],
})
export class MipsQuestionnaireComponent implements OnInit, OnDestroy {
  @Input() parentFormGroup?: FormGroup;

  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(UserService);
  private readonly destroy$ = new Subject<void>();
  private readonly cancelLoad$ = new Subject<void>();
  private readonly ctrlContainer = inject(FormGroupDirective);

  performanceYear = getCurrentPerformanceYear();
  availableYears = getAvailablePerformanceYears();
  mipsForm!: FormGroup;
  private formInitialized = false;
  private lastLoadedQuestionnaireOrgId: number | null = null;
  isLoadingData = false;

  @Output() yearChange = new EventEmitter<number>();

  get showYearSelector(): boolean {
    return this.availableYears.length > 1;
  }

  get isSelectedYearClosed(): boolean {
    return isSubmissionsClosed() && this.performanceYear !== getCurrentPerformanceYear();
  }

  activeUser = toSignal(this.store.select(selectActiveUser));
  currentPacket = toSignal(this.store.select(selectActivePacket));
  private readonly storedQuestionnaire = toSignal(this.store.select(selectEffectiveQuestionnaire));
  isSuperAdmin$ = this.userService.hasRequiredRoles(ROLE_GROUPS.SUPER_USER);
  isExporting = false;

  get showSpecialStatusCheckboxes(): boolean {
    return this.mipsForm?.get("specialStatus")?.value === "yes";
  }

  get showReweightingCategories(): boolean {
    return this.mipsForm?.get("reweighting")?.value === "yes";
  }

  get showThirdPartyDetails(): boolean {
    return this.mipsForm?.get("thirdPartyData")?.value === "yes";
  }

  ngOnInit(): void {
    this.mipsForm = this.fb.group({
      id: [null],
      requiredToReport: ["", Validators.required],
      reportingType: ["", Validators.required],
      reportingLevel: this.fb.group(
        {
          individual: [false],
          group: [false],
          subgroup: [false],
        },
        { validators: atLeastOneCheckboxValidator }
      ),
      specialStatus: ["", Validators.required],
      specialStatusCheckboxes: this.fb.group({
        small: [false],
        hpsa: [false],
        rural: [false],
        nonPatientFacing: [false],
      }),
      reweighting: ["", Validators.required],
      reweightingCategories: this.fb.group({
        quality: [false],
        pi: [false],
        ia: [false],
        cost: [false],
      }),
      thirdPartyData: ["", Validators.required],
      thirdPartyDataDetails: [""],
      thirdPartyDataCategories: this.fb.group({
        quality: [false],
        promotingInteroperability: [false],
        improvementActivities: [false],
      }),
    });

    this.setupConditionalValidation();

    this.store
      .select(selectEffectiveQuestionnaire)
      .pipe(
        filter(
          (q): q is MipsQuestionnaire =>
            !!q &&
            (q.id !== undefined ||
              q.requiredToReport !== "" ||
              q.reportingType !== "")
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        const incomingOrgId = data.organizationID ?? null;
        if (!this.formInitialized || incomingOrgId !== this.lastLoadedQuestionnaireOrgId) {
          this.lastLoadedQuestionnaireOrgId = incomingOrgId;
          this.formInitialized = false;
          this.handleQuestionnaireData(data);
        }
      });

    setTimeout(() => {
      this.parentForm = this.ctrlContainer.form;
      if (this.parentForm) {
        this.parentForm.addControl("mipsInfo", this.mipsForm);
      }
    });

    this.mipsForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateParentFormValidity();
    });

    setTimeout(() => {
      this.updateParentFormValidity();
    });

    this.mipsForm.valueChanges
      .pipe(debounceTime(2000), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.mipsForm.valid && this.effectiveOrgId && !this.isLoadingData) {
          this.autoSaveQuestionnaire();
        }
      });
  }

  ngOnDestroy(): void {
    const parentForm = this.ctrlContainer.control as FormGroup;
    parentForm?.removeControl("mipsInfo");
    this.cancelLoad$.next();
    this.cancelLoad$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @Input() set syncedPerformanceYear(year: number) {
    if (!year || year === this.performanceYear) return;
    this.loadYear(year);
  }

  onYearChange(year: number): void {
    this.loadYear(year);
    this.yearChange.emit(year);
  }

  private loadYear(year: number): void {
    this.cancelLoad$.next();
    this.performanceYear = year;
    this.isLoadingData = true;
    this.formInitialized = false;
    this.mipsForm.reset({
      id: null,
      requiredToReport: "",
      reportingType: "",
      reportingLevel: { individual: false, group: false, subgroup: false },
      specialStatus: "",
      specialStatusCheckboxes: { small: false, hpsa: false, rural: false, nonPatientFacing: false },
      reweighting: "",
      reweightingCategories: { quality: false, pi: false, ia: false, cost: false },
      thirdPartyData: "",
      thirdPartyDataDetails: "",
      thirdPartyDataCategories: { quality: false, promotingInteroperability: false, improvementActivities: false },
    });

    this.store.dispatch(MipsQuestionnaireActions.resetForm());
    this.store.dispatch(MipsEligibilityActions.clearEligibilityResults());

    const user = this.activeUser();
    if (!user?.orgId) {
      this.applyInitialValidators();
      this.isLoadingData = false;
      return;
    }

    this.store.dispatch(
      MipsQuestionnaireActions.loadMIPSQuestionnaire({
        orgId: user.orgId.toString(),
        performanceYear: year,
      })
    );

    this.actions$
      .pipe(
        ofType(MipsQuestionnaireActions.loadMIPSQuestionnaireSuccess),
        take(1),
        takeUntil(this.cancelLoad$),
        takeUntil(this.destroy$)
      )
      .subscribe(({ data }) => {
        this.handleQuestionnaireData(data);
      });

    this.actions$
      .pipe(
        ofType(MipsQuestionnaireActions.loadMIPSQuestionnaireFailure),
        take(1),
        takeUntil(this.cancelLoad$),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.applyInitialValidators();
        this.isLoadingData = false;
      });
  }

  private handleQuestionnaireData(data: MipsQuestionnaire): void {
    this.loadFromStore(data);
    this.applyInitialValidators();
    this.isLoadingData = false;
    this.updateParentFormValidity();
  }

  private get parentForm(): FormGroup {
    return this.ctrlContainer.form;
  }

  private set parentForm(_form: FormGroup) {}

  private loadFromStore(data: MipsQuestionnaire): void {
    if (this.formInitialized) return;

    this.formInitialized = true;

    const formData = {
      id: data.id,
      requiredToReport: data.requiredToReport || "",
      reportingType: (REPORTING_TYPES as readonly string[]).includes(data.reportingType) ? data.reportingType : "",
      reportingLevel: {
        individual: data.reportingLevelIndividual === "yes",
        group: data.reportingLevelGroup === "yes",
        subgroup: data.reportingLevelSubgroup === "yes",
      },
      specialStatus: data.specialStatus || "",
      specialStatusCheckboxes: {
        small: data.specialStatusSmall === "yes",
        hpsa: data.specialStatusHpsa === "yes",
        rural: data.specialStatusRural === "yes",
        nonPatientFacing: data.specialStatusNonPatientFacing === "yes",
      },
      reweighting: data.appliedHardship || "",
      reweightingCategories: {
        quality: data.hardshipQuality === "yes",
        pi: data.hardshipPromotingInteroperability === "yes",
        ia: data.hardShipImprovementActivities === "yes",
        cost: data.hardshipCost === "yes",
      },
      thirdPartyData: data.thirdPartyData || "",
      thirdPartyDataDetails: data.thirdPartyDataDetails || "",
      thirdPartyDataCategories: {
        quality: data.thirdPartyDataQuality === "yes",
        promotingInteroperability: data.thirdPartyDataPromotingInteroperability === "yes",
        improvementActivities: data.thirdPartyDataImprovementActivities === "yes",
      },
    };

    this.mipsForm.patchValue(formData, { emitEvent: false });
  }

  private applyInitialValidators(): void {
    const specialStatusCheckboxesGroup = this.mipsForm.get("specialStatusCheckboxes") as FormGroup;
    const reweightingCategoriesGroup = this.mipsForm.get("reweightingCategories") as FormGroup;
    const thirdPartyDataDetailsControl = this.mipsForm.get("thirdPartyDataDetails");
    const thirdPartyDataCategoriesGroup = this.mipsForm.get("thirdPartyDataCategories") as FormGroup;

    if (this.showSpecialStatusCheckboxes) {
      specialStatusCheckboxesGroup.setValidators(atLeastOneCheckboxValidator);
    } else {
      specialStatusCheckboxesGroup.clearValidators();
    }
    specialStatusCheckboxesGroup.updateValueAndValidity({ emitEvent: false });

    if (this.showReweightingCategories) {
      reweightingCategoriesGroup.setValidators(atLeastOneCheckboxValidator);
    } else {
      reweightingCategoriesGroup.clearValidators();
    }
    reweightingCategoriesGroup.updateValueAndValidity({ emitEvent: false });

    if (this.showThirdPartyDetails) {
      thirdPartyDataDetailsControl?.setValidators([Validators.required, Validators.maxLength(512)]);
      thirdPartyDataCategoriesGroup.setValidators(atLeastOneCheckboxValidator);
    } else {
      thirdPartyDataDetailsControl?.clearValidators();
      thirdPartyDataCategoriesGroup.clearValidators();
    }
    thirdPartyDataDetailsControl?.updateValueAndValidity({ emitEvent: false });
    thirdPartyDataCategoriesGroup.updateValueAndValidity({ emitEvent: false });
  }

  private setupConditionalValidation(): void {
    this.mipsForm
      .get("specialStatus")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const checkboxesGroup = this.mipsForm.get("specialStatusCheckboxes") as FormGroup;

        if (this.showSpecialStatusCheckboxes) {
          checkboxesGroup.setValidators(atLeastOneCheckboxValidator);
        } else {
          checkboxesGroup.clearValidators();
          checkboxesGroup.reset({ small: false, hpsa: false, rural: false, nonPatientFacing: false });
        }

        checkboxesGroup.updateValueAndValidity();
      });

    this.mipsForm
      .get("reweighting")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const categoriesGroup = this.mipsForm.get("reweightingCategories") as FormGroup;

        if (this.showReweightingCategories) {
          categoriesGroup.setValidators(atLeastOneCheckboxValidator);
        } else {
          categoriesGroup.clearValidators();
          categoriesGroup.reset({ quality: false, pi: false, ia: false, cost: false });
        }

        categoriesGroup.updateValueAndValidity();
      });

    this.mipsForm
      .get("thirdPartyData")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const detailsControl = this.mipsForm.get("thirdPartyDataDetails");
        const categoriesGroup = this.mipsForm.get("thirdPartyDataCategories") as FormGroup;

        if (this.showThirdPartyDetails) {
          detailsControl?.setValidators([Validators.required, Validators.maxLength(512)]);
          categoriesGroup.setValidators(atLeastOneCheckboxValidator);
        } else {
          detailsControl?.clearValidators();
          detailsControl?.reset("");
          categoriesGroup.clearValidators();
          categoriesGroup.reset({ quality: false, promotingInteroperability: false, improvementActivities: false });
        }

        detailsControl?.updateValueAndValidity();
        categoriesGroup.updateValueAndValidity();
      });
  }

  private get effectiveOrgId(): string | undefined {
    const packetOrgId = this.currentPacket()?.orgId;
    return packetOrgId || this.activeUser()?.orgId?.toString();
  }

  private autoSaveQuestionnaire(): void {
    if (this.isSelectedYearClosed) return;
    const orgId = this.effectiveOrgId;
    if (!orgId) return;

    const questionnaire = this.convertFormToDatabase(this.mipsForm.getRawValue());

    this.store.dispatch(
      questionnaire.id
        ? MipsQuestionnaireActions.updateMIPSQuestionnaire({
            mipsQuestionnaire: questionnaire,
            orgId,
            performanceYear: this.performanceYear,
          })
        : MipsQuestionnaireActions.submitMIPSQuestionnaire({
            mipsQuestionnaire: questionnaire,
            orgId,
            performanceYear: this.performanceYear,
          })
    );
  }

  onExportQuestionnaires(): void {
    const orgId = this.effectiveOrgId;
    if (orgId) {
      this.store.dispatch(
        MipsQuestionnaireActions.exportAllQuestionnaires({
          orgId,
          performanceYear: this.performanceYear,
        })
      );
    }
  }

  private updateParentFormValidity(): void {
    const parentForm = this.ctrlContainer.control as FormGroup;
    if (!parentForm) return;

    this.mipsForm.valid
      ? parentForm.setErrors(null)
      : parentForm.setErrors({ incomplete: true });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.mipsForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  convertFormToDatabase(formValue: any): MipsQuestionnaire {
    const reportingLevel = formValue.reportingLevel || {};
    const specialStatusCheckboxes = formValue.specialStatusCheckboxes || {};
    const reweightingCategories = formValue.reweightingCategories || {};
    const thirdPartyDataCategories = formValue.thirdPartyDataCategories || {};
    const questionnaire = this.storedQuestionnaire();

    return {
      id: formValue.id,
      measurementYear: this.performanceYear,
      organizationID: parseInt(this.effectiveOrgId || "0"),
      requiredToReport: formValue.requiredToReport,
      reportingType: formValue.reportingType,
      reportingLevelIndividual: reportingLevel.individual ? "yes" : "no",
      reportingLevelGroup: reportingLevel.group ? "yes" : "no",
      reportingLevelSubgroup: reportingLevel.subgroup ? "yes" : "no",
      specialStatus: formValue.specialStatus,
      specialStatusSmall: specialStatusCheckboxes.small ? "yes" : "no",
      specialStatusHpsa: specialStatusCheckboxes.hpsa ? "yes" : "no",
      specialStatusRural: specialStatusCheckboxes.rural ? "yes" : "no",
      specialStatusNonPatientFacing: specialStatusCheckboxes.nonPatientFacing ? "yes" : "no",
      appliedHardship: formValue.reweighting,
      hardshipQuality: reweightingCategories.quality ? "yes" : "no",
      hardshipPromotingInteroperability: reweightingCategories.pi ? "yes" : "no",
      hardShipImprovementActivities: reweightingCategories.ia ? "yes" : "no",
      hardshipCost: reweightingCategories.cost ? "yes" : "no",
      thirdPartyData: formValue.thirdPartyData,
      thirdPartyDataDetails: formValue.thirdPartyDataDetails || null,
      thirdPartyDataQuality: thirdPartyDataCategories.quality ? "yes" : "no",
      thirdPartyDataPromotingInteroperability: thirdPartyDataCategories.promotingInteroperability ? "yes" : "no",
      thirdPartyDataImprovementActivities: thirdPartyDataCategories.improvementActivities ? "yes" : "no",
      piAuditAttestation: questionnaire?.piAuditAttestation ?? null,
      oncDirectReview: questionnaire?.oncDirectReview ?? null,
      interoperabilityAttestation: questionnaire?.interoperabilityAttestation ?? null,
      securityRiskAnalysis: questionnaire?.securityRiskAnalysis ?? null,
      securityRiskAnalysisImplemented: questionnaire?.securityRiskAnalysisImplemented ?? null,
      saferGuidesAttestation: questionnaire?.saferGuidesAttestation ?? null,
    } as MipsQuestionnaire;
  }
}
